import React, { useCallback, useContext, useMemo } from 'react'
import CartReviewTile from './CartReviewTile'
import CartReviewBanner from './CartReviewBanner'
import useUserCartSubscriptionItemData from 'hooks/UserCart/useUserCartSubscriptionItemData'
import LuxPlusProductPlaceholder from 'luxPlus/components/LuxPlusProductPlaceholder'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlFormatter, addYears } from 'lib/datetime/dateUtils'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import TextLink from 'components/Luxkit/TextLink'
import LuxPlusSubscriptionCancellationModal from 'luxPlus/components/LuxPlusSubscriptionCancellationModal'
import ModalContext from 'contexts/ModalContext'
import { CartItemData } from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { calculateHotelLuxPlusDetails, filterOutAddonItems } from './CartReviewUtils'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import LuxPlusCartReviewPriceStack from './LuxPlusCartReviewPriceStack'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/AuthActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
interface Props {
  item: App.Checkout.LuxPlusSubscriptionItem
  cartItemsData: Array<CartItemData>
  tileVariant?: 'default' | 'condensed'
}

function LuxPlusCartReviewTile({ item, cartItemsData, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const data = useUserCartSubscriptionItemData(item)

  if (!!data.error && !data.loading && !data.isSavedForLater) {
    dispatch(userCartItemsSaveForLater([item.itemId]))
  }

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater(nonNullable([item.itemId])))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete(nonNullable([item.itemId])))
  }, [dispatch, item])

  const cancellationInfo = useMemo(() => {
    return `Renews ${intlFormatter(addYears(new Date(), 1), INTL_DMY_CASUAL_SHORT_FORMAT)}.`
  }, [])

  const { totalSavings } = useMemo(() => {
    return calculateHotelLuxPlusDetails(filterOutAddonItems(cartItemsData))
  }, [cartItemsData])

  const onShowCancellationPolicy = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.stopPropagation()
    event.preventDefault()
    showModal(<LuxPlusSubscriptionCancellationModal />)
  }, [showModal])

  const memberPriceFilter: App.OfferListFilters = {
    offerTypes: ['hotel', 'tactical_ao_hotel'],
    luxPlusFeatures: ['Member prices'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
  }

  const link = `/search?${getSearchQueryFromFilter(memberPriceFilter)}`

  return (
    <CartReviewTile
      itemId={item.itemId}
      fetching={data.loading}
      to="/lux-plus"
      imageElement={<LuxPlusProductPlaceholder />}
      title="LuxPlus+ Yearly Membership"
      metaData={<BodyText variant="medium" colour="neutral-two">{cancellationInfo}</BodyText>}
      cancellationPolicy={<TextLink weight="bold" variant="highlight" size="medium" endIcon={<LineInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
        Cancel renewal anytime
      </TextLink>}
      price={
        <LuxPlusCartReviewPriceStack
          price={data.itemTotals?.price ?? 0}
          joiningFee={data.joiningFee?.amount ?? 0}
          waiveFee={!!data.joiningFee?.waived}
          tileVariant={tileVariant}
        />
      }
      banner={
        <CartReviewBanner
          variant={(!data.isDeleted && data.isSavedForLater) ? 'luxplus' : undefined}
          link={link}
          savings={totalSavings}
        />
      }
      isBookable
      saveForLater={data.isSavedForLater}
      deleted={data.isDeleted}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
      tileVariant={tileVariant}
    />
  )
}

export default LuxPlusCartReviewTile
